import React, { useState, useEffect, useRef } from "react";
import ItemPriceStats from "./ItemPriceStats";
import FormField from "../forms/FormField";
import FileUpload from "../forms/FileUpload";
import LoadingSpinner from "../common/LoadingSpinner";
import {
  createItem,
  updateItem,
  deleteItem,
  getItems,
  searchItems,
  getCityPrices,
  setItemPrice as saveItemPrice,
  getItemPriceHistory,
} from "../../services/itemService";
import {
  ITEM_STATUS,
  ITEM_UNITS,
  SUCCESS_MESSAGES,
} from "../../utils/constants";
import { useAuth } from "../../contexts/AuthContext";
import firestoreService from "../../services/firestoreService";
import { loadAllLocations } from "../../services/locationService";

const ItemManager = ({ categories, onSuccess, onError }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");

  // City-based pricing state
  const [userCityId, setUserCityId] = useState(null);
  const [userCityName, setUserCityName] = useState("");
  const [cityPrices, setCityPrices] = useState({});
  const [itemPrice, setItemPrice] = useState("");
  const [itemStock, setItemStock] = useState("");
  const [itemUnit, setItemUnit] = useState("piece");

  // Admin city selection
  const [isAdmin, setIsAdmin] = useState(false);
  const [allCities, setAllCities] = useState([]);
  const [selectedPriceCityId, setSelectedPriceCityId] = useState("");

  // Admin view city (for filtering prices in the list)
  const [viewCityId, setViewCityId] = useState("");

  // Ref to scroll to form on edit
  const formRef = useRef(null);

  // Statistics modal state
  const [statsItem, setStatsItem] = useState(null); // item being viewed
  const [statsHistory, setStatsHistory] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    categoryId: "",
    minStockLevel: "",
    barcode: "",
    brand: "",
    weight: "",
    dimensions: "",
    nutritionalInfo: "",
    ingredients: "",
    allergens: "",
    expiryDate: "",
    status: ITEM_STATUS.ACTIVE,
    imageFile: null,
    imageUrl: "",
  });

  useEffect(() => {
    loadItems();
    loadUserCity();
  }, []);

  const loadUserCity = async () => {
    if (!user?.email) return;
    try {
      const account = await firestoreService.getUserAccount(user.email);
      const adminFlag = account?.isAdmin === true;
      setIsAdmin(adminFlag);

      const locs = await loadAllLocations();

      if (adminFlag) {
        // Admin: load all cities for the dropdown
        setAllCities(locs.cities || []);
        // Admin has no fixed city — cityPrices stays empty until they pick a city
      } else {
        // Moderator/regular: use their own city
        const cityId = await firestoreService.getUserCityId(user.email);
        if (cityId) {
          setUserCityId(cityId);
          setUserCityName(locs.cityMap?.[cityId]?.name || cityId);
          const prices = await getCityPrices(cityId);
          setCityPrices(prices);
        }
      }
    } catch (e) {
      console.warn("Could not load user city:", e.message);
    }
  };

  useEffect(() => {
    filterItems();
  }, [items, searchTerm, selectedCategory]);

  // When admin picks a view city, reload prices for that city
  useEffect(() => {
    if (isAdmin && viewCityId) {
      getCityPrices(viewCityId)
        .then(setCityPrices)
        .catch(() => {});
    }
  }, [isAdmin, viewCityId]);

  const loadItems = async () => {
    try {
      setLoading(true);
      const itemsData = await getItems();
      setItems(itemsData);
    } catch (error) {
      onError("Failed to load items");
      console.error("Error loading items:", error);
    } finally {
      setLoading(false);
    }
  };

  const filterItems = () => {
    let filtered = items;

    if (selectedCategory) {
      filtered = filtered.filter(
        (item) => item.categoryId === selectedCategory,
      );
    }

    if (searchTerm) {
      filtered = filtered.filter(
        (item) =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.barcode?.includes(searchTerm) ||
          item.brand?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    setFilteredItems(filtered);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      onError("Item name is required");
      return;
    }
    if (!formData.categoryId) {
      onError("Please select a category");
      return;
    }

    try {
      setLoading(true);

      const { imageFile, imageUrl, ...cleanItemData } = formData;

      const itemData = {
        ...cleanItemData,
        minStockLevel: parseInt(formData.minStockLevel) || 0,
        weight: formData.weight ? parseFloat(formData.weight) : null,
      };

      let savedItemId;
      if (editingItem) {
        await updateItem(editingItem.id, { ...itemData, imageUrl }, imageFile);
        savedItemId = editingItem.id;
        onSuccess(SUCCESS_MESSAGES.ITEM_UPDATED);
      } else {
        const created = await createItem(itemData, imageFile);
        savedItemId = created.id;
        onSuccess(SUCCESS_MESSAGES.ITEM_CREATED);
      }

      // Save city-based price
      const priceCityId = isAdmin ? selectedPriceCityId : userCityId;
      if (priceCityId && itemPrice && parseFloat(itemPrice) > 0) {
        await saveItemPrice(
          savedItemId,
          priceCityId,
          parseFloat(itemPrice),
          itemUnit,
          parseInt(itemStock) || 0,
        );
        // Refresh city prices for display (use the effective city)
        const prices = await getCityPrices(priceCityId);
        setCityPrices(prices);
        if (isAdmin) setUserCityId(priceCityId); // update display city to the one just priced
      }

      resetForm();
      loadItems();
    } catch (error) {
      onError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description || "",
      categoryId: item.categoryId,
      minStockLevel: item.minStockLevel?.toString() || "",
      barcode: item.barcode || "",
      brand: item.brand || "",
      weight: item.weight?.toString() || "",
      dimensions: item.dimensions || "",
      nutritionalInfo: item.nutritionalInfo || "",
      ingredients: item.ingredients || "",
      allergens: item.allergens || "",
      expiryDate: item.expiryDate || "",
      status: item.status || ITEM_STATUS.ACTIVE,
      imageFile: null,
      imageUrl: item.imageUrl || "",
    });
    // Load existing city price for this item
    const effectiveCityId = isAdmin ? viewCityId : userCityId;
    if (effectiveCityId && cityPrices[item.id]) {
      setItemPrice(cityPrices[item.id].price?.toString() || "");
      setItemStock(cityPrices[item.id].stock_quantity?.toString() || "");
      setItemUnit(cityPrices[item.id].unit || "piece");
    } else {
      setItemPrice("");
      setItemStock("");
      setItemUnit("piece");
    }
    // Pre-fill admin city selector with current view city
    if (isAdmin && viewCityId) setSelectedPriceCityId(viewCityId);
    setShowForm(true);
    // Scroll to form
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm("Are you sure you want to delete this item?")) {
      return;
    }

    try {
      setLoading(true);
      await deleteItem(itemId);
      onSuccess(SUCCESS_MESSAGES.ITEM_DELETED);
      loadItems();
    } catch (error) {
      onError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      categoryId: "",
      minStockLevel: "",
      barcode: "",
      brand: "",
      weight: "",
      dimensions: "",
      nutritionalInfo: "",
      ingredients: "",
      allergens: "",
      expiryDate: "",
      status: ITEM_STATUS.ACTIVE,
      imageFile: null,
      imageUrl: "",
    });
    setEditingItem(null);
    setItemPrice("");
    setItemStock("");
    setItemUnit("piece");
    setSelectedPriceCityId("");
    setShowForm(false);
  };

  const openStats = async (item) => {
    setStatsItem(item);
    const statsCityId = isAdmin ? viewCityId : userCityId;
    if (!statsCityId) {
      setStatsHistory([]);
      setStatsLoading(false);
      return;
    }
    setStatsLoading(true);
    try {
      const history = await getItemPriceHistory(item.id, statsCityId);
      setStatsHistory(history);
    } catch (e) {
      setStatsHistory([]);
    } finally {
      setStatsLoading(false);
    }
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);
    return category ? category.name : "Unknown Category";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case ITEM_STATUS.ACTIVE:
        return "success";
      case ITEM_STATUS.INACTIVE:
        return "secondary";
      case ITEM_STATUS.OUT_OF_STOCK:
        return "danger";
      default:
        return "secondary";
    }
  };

  if (categories.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-icon">📁</div>
        <h3>No Categories Available</h3>
        <p>Please create categories first before adding items</p>
      </div>
    );
  }

  return (
    <>
      <div className="item-manager">
        {/* Header */}
        <div className="section-header">
          <h2>Item Management</h2>
          <button
            onClick={() => setShowForm(!showForm)}
            className="btn btn-primary"
            disabled={loading}
          >
            {showForm ? "Cancel" : "+ Add Item"}
          </button>
        </div>

        {/* Search and Filters */}
        <div className="filters-section">
          {/* Admin: city picker to view prices */}
          {isAdmin && (
            <div
              style={{
                marginBottom: "0.75rem",
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
              }}
            >
              <label
                style={{
                  fontSize: "0.875rem",
                  fontWeight: "500",
                  color: "var(--text-secondary)",
                  whiteSpace: "nowrap",
                }}
              >
                View prices for city:
              </label>
              <select
                className="form-select"
                value={viewCityId}
                onChange={(e) => setViewCityId(e.target.value)}
                style={{ maxWidth: "260px" }}
              >
                <option value="">— Select city —</option>
                {allCities.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {viewCityId && (
                <span
                  style={{
                    fontSize: "0.8125rem",
                    color: "var(--color-success)",
                  }}
                >
                  ✓ Showing prices for{" "}
                  {allCities.find((c) => c.id === viewCityId)?.name}
                </span>
              )}
            </div>
          )}
          <div className="filter-row">
            <FormField
              label="Search Items"
              type="text"
              name="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, description, barcode, or brand..."
            />
            <FormField
              label="Filter by Category"
              type="select"
              name="categoryFilter"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              options={[
                { value: "", label: "All Categories" },
                ...categories.map((cat) => ({
                  value: cat.id,
                  label: cat.name,
                })),
              ]}
            />
          </div>
        </div>

        {/* Item Form */}
        {showForm && (
          <div className="form-section" ref={formRef}>
            <h3>{editingItem ? "Edit Item" : "Add New Item"}</h3>
            <form onSubmit={handleSubmit} className="item-form">
              {/* Basic Information */}
              <div className="form-section-title">Basic Information</div>
              <div className="form-row">
                <FormField
                  label="Item Name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="e.g., Organic Bananas"
                  required
                />
                <FormField
                  label="Category"
                  type="select"
                  name="categoryId"
                  value={formData.categoryId}
                  onChange={(e) =>
                    handleInputChange("categoryId", e.target.value)
                  }
                  options={[
                    { value: "", label: "Select Category" },
                    ...categories.map((cat) => ({
                      value: cat.id,
                      label: cat.name,
                    })),
                  ]}
                  required
                />
              </div>

              <FormField
                label="Description"
                type="textarea"
                name="description"
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder="Brief description of the item"
                rows={3}
              />

              {/* Pricing and Stock */}
              <div className="form-section-title">Pricing & Stock</div>

              {isAdmin ? (
                /* Admin: city dropdown + price fields */
                <>
                  <div className="form-field" style={{ marginBottom: "1rem" }}>
                    <label className="form-label">
                      City for Price Assignment
                    </label>
                    <select
                      className="form-select"
                      value={selectedPriceCityId}
                      onChange={(e) => setSelectedPriceCityId(e.target.value)}
                      disabled={loading}
                    >
                      <option value="">— Select a city —</option>
                      {allCities.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {selectedPriceCityId ? (
                    <div className="form-row">
                      <FormField
                        label={`Price (${allCities.find((c) => c.id === selectedPriceCityId)?.name || selectedPriceCityId})`}
                        type="number"
                        name="itemPrice"
                        value={itemPrice}
                        onChange={(e) => setItemPrice(e.target.value)}
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                      />
                      <FormField
                        label="Unit"
                        type="select"
                        name="unit"
                        value={itemUnit}
                        onChange={(e) => setItemUnit(e.target.value)}
                        options={ITEM_UNITS}
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        padding: "0.75rem",
                        background: "var(--bg-tertiary)",
                        borderRadius: "0.375rem",
                        marginBottom: "1rem",
                        fontSize: "0.875rem",
                        color: "var(--text-secondary)",
                      }}
                    >
                      ℹ️ Select a city above to assign a price for that city.
                    </div>
                  )}
                </>
              ) : userCityId ? (
                <div className="form-row">
                  <FormField
                    label={`Price (${userCityName || userCityId})`}
                    type="number"
                    name="itemPrice"
                    value={itemPrice}
                    onChange={(e) => setItemPrice(e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                  />
                  <FormField
                    label="Unit"
                    type="select"
                    name="unit"
                    value={itemUnit}
                    onChange={(e) => setItemUnit(e.target.value)}
                    options={ITEM_UNITS}
                  />
                </div>
              ) : (
                <div
                  style={{
                    padding: "0.75rem",
                    background: "var(--bg-tertiary)",
                    borderRadius: "0.375rem",
                    marginBottom: "1rem",
                    fontSize: "0.875rem",
                    color: "var(--text-secondary)",
                  }}
                >
                  ℹ️ No city assigned to your account. Price cannot be set.
                  Contact an admin to assign your city.
                </div>
              )}

              <div className="form-row">
                <FormField
                  label="Stock Quantity"
                  type="number"
                  name="stockQuantity"
                  value={itemStock}
                  onChange={(e) => setItemStock(e.target.value)}
                  placeholder="0"
                  min="0"
                />
                <FormField
                  label="Minimum Stock Level"
                  type="number"
                  name="minStockLevel"
                  value={formData.minStockLevel}
                  onChange={(e) =>
                    handleInputChange("minStockLevel", e.target.value)
                  }
                  placeholder="0"
                  min="0"
                />
              </div>

              {/* Product Details */}
              <div className="form-section-title">Product Details</div>
              <div className="form-row">
                <FormField
                  label="Brand"
                  type="text"
                  name="brand"
                  value={formData.brand}
                  onChange={(e) => handleInputChange("brand", e.target.value)}
                  placeholder="e.g., Organic Valley"
                />
                <FormField
                  label="Barcode"
                  type="text"
                  name="barcode"
                  value={formData.barcode}
                  onChange={(e) => handleInputChange("barcode", e.target.value)}
                  placeholder="e.g., 1234567890123"
                />
              </div>

              <div className="form-row">
                <FormField
                  label="Weight (if applicable)"
                  type="number"
                  name="weight"
                  value={formData.weight}
                  onChange={(e) => handleInputChange("weight", e.target.value)}
                  placeholder="0.0"
                  step="0.01"
                  min="0"
                />
                <FormField
                  label="Dimensions"
                  type="text"
                  name="dimensions"
                  value={formData.dimensions}
                  onChange={(e) =>
                    handleInputChange("dimensions", e.target.value)
                  }
                  placeholder="e.g., 10cm x 5cm x 3cm"
                />
              </div>

              {/* Additional Information */}
              <div className="form-section-title">Additional Information</div>
              <FormField
                label="Ingredients"
                type="textarea"
                name="ingredients"
                value={formData.ingredients}
                onChange={(e) =>
                  handleInputChange("ingredients", e.target.value)
                }
                placeholder="List of ingredients (if applicable)"
                rows={2}
              />

              <FormField
                label="Allergens"
                type="text"
                name="allergens"
                value={formData.allergens}
                onChange={(e) => handleInputChange("allergens", e.target.value)}
                placeholder="e.g., Contains nuts, dairy"
              />

              <FormField
                label="Nutritional Information"
                type="textarea"
                name="nutritionalInfo"
                value={formData.nutritionalInfo}
                onChange={(e) =>
                  handleInputChange("nutritionalInfo", e.target.value)
                }
                placeholder="Nutritional facts (if applicable)"
                rows={3}
              />

              <div className="form-row">
                <FormField
                  label="Expiry Date"
                  type="date"
                  name="expiryDate"
                  value={formData.expiryDate}
                  onChange={(e) =>
                    handleInputChange("expiryDate", e.target.value)
                  }
                />
                <FormField
                  label="Status"
                  type="select"
                  name="status"
                  value={formData.status}
                  onChange={(e) => handleInputChange("status", e.target.value)}
                  options={[
                    { value: ITEM_STATUS.ACTIVE, label: "Active" },
                    { value: ITEM_STATUS.INACTIVE, label: "Inactive" },
                    { value: ITEM_STATUS.OUT_OF_STOCK, label: "Out of Stock" },
                  ]}
                />
              </div>

              {/* Image Upload */}
              <div className="form-section-title">Item Image</div>
              <FileUpload
                label="Item Image"
                name="imageFile"
                accept="image/*"
                value={formData.imageFile}
                onChange={(e) => handleInputChange("imageFile", e.target.value)}
                currentFileUrl={formData.imageUrl}
              />

              <div className="form-actions">
                <button
                  type="button"
                  onClick={resetForm}
                  className="btn btn-secondary"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? (
                    <LoadingSpinner size="small" />
                  ) : editingItem ? (
                    "Update Item"
                  ) : (
                    "Create Item"
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Items List */}
        <div className="items-list">
          {filteredItems.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🛒</div>
              <h3>No Items Found</h3>
              <p>
                {searchTerm || selectedCategory
                  ? "Try adjusting your search or filters"
                  : "Create your first item to get started"}
              </p>
            </div>
          ) : (
            <div className="items-grid">
              {filteredItems.map((item) => (
                <div key={item.id} className="item-card">
                  <div className="item-image">
                    {item.imageUrl ? (
                      <img src={item.imageUrl} alt={item.name} />
                    ) : (
                      <div className="no-image">📦</div>
                    )}
                  </div>

                  <div className="item-content">
                    <div className="item-header">
                      <h4>{item.name}</h4>
                      <span
                        className={`status-badge ${getStatusColor(item.status)}`}
                      >
                        {item.status}
                      </span>
                    </div>

                    <p className="item-category">
                      {getCategoryName(item.categoryId)}
                    </p>

                    {item.description && (
                      <p className="item-description">{item.description}</p>
                    )}

                    <div className="item-details">
                      <div className="detail-row">
                        <span className="label">Price:</span>
                        <span className="value">
                          {cityPrices[item.id]
                            ? `${cityPrices[item.id].price} / ${cityPrices[item.id].unit || "—"}`
                            : "—"}
                        </span>
                      </div>
                      {item.brand && (
                        <div className="detail-row">
                          <span className="label">Brand:</span>
                          <span className="value">{item.brand}</span>
                        </div>
                      )}
                      <div className="detail-row">
                        <span className="label">Stock:</span>
                        <span className="value">
                          {cityPrices[item.id]?.stock_quantity ?? 0}
                        </span>
                      </div>
                      {item.barcode && (
                        <div className="detail-row">
                          <span className="label">Barcode:</span>
                          <span className="value">{item.barcode}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="item-actions">
                    <button
                      onClick={() => openStats(item)}
                      className="btn btn-sm btn-secondary"
                      disabled={loading}
                      title="Price History"
                      style={{ fontSize: "1rem" }}
                    >
                      📊
                    </button>
                    <button
                      onClick={() => handleEdit(item)}
                      className="btn btn-sm btn-secondary"
                      disabled={loading}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="btn btn-sm btn-danger"
                      disabled={loading}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <ItemPriceStats
        item={statsItem}
        cityName={
          isAdmin
            ? allCities.find((c) => c.id === viewCityId)?.name
            : userCityName
        }
        cityId={isAdmin ? viewCityId : userCityId}
        history={statsHistory}
        loading={statsLoading}
        onClose={() => setStatsItem(null)}
      />
    </>
  );
};

export default ItemManager;
